import React, { useState, ReactNode, useCallback, useMemo } from 'react';
import { compareByRenderOrderDesc, ControllableElement } from '@bynder-studio/render-core';
import { useTranslate } from '@bynder/localization';
import { notify } from '@bynder/design-system';
import componentService, { AddComponentRequest, PartialComponent } from 'packages/services/componentService';
import componentCollectionService from 'packages/services/componentCollectionService';
import ComponentOverview from './ComponentOverview/ComponentOverview';
import ComponentModal from './ComponentModal/ComponentModal';
import { ComponentModalMode } from './ComponentModal/types';
import useComponents from './useComponents';
import useEditor from '~/hooks/useEditor';
import { ComponentsContext, ComponentsContextValue } from './useComponentsContext';

export { useComponentsContext } from './useComponentsContext';

interface ComponentsProviderProps {
    children: ReactNode;
}

export const ComponentsProvider = ({ children }: ComponentsProviderProps) => {
    const { translate } = useTranslate();
    const [isOpen, setIsOpen] = useState(false);
    const [onApplySuccess, setOnApplySuccess] = useState<(() => void) | null>(null);
    const { creativeType } = useEditor();
    const { applyComponent, createComponent } = useComponents();

    // ComponentModal state
    const [isComponentModalOpen, setComponentModalOpen] = useState(false);
    const [componentModalMode, setComponentModalMode] = useState<ComponentModalMode>('create');
    const [selectedElements, setSelectedElements] = useState<ControllableElement[]>([]);
    const [selectedComponent, setSelectedComponent] = useState<PartialComponent | null>(null);
    const [selectedCollectionId, setSelectedCollectionId] = useState<number | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [onComponentSaveSuccess, setOnComponentSaveSuccess] = useState<((resetCollection: boolean) => void) | null>(
        null,
    );

    const isImageDesign = useMemo(() => creativeType === 'IMAGE', [creativeType]);

    const openModal = useCallback((callback: () => void) => {
        setOnApplySuccess(() => callback);
        setIsOpen(true);
    }, []);

    const closeModal = useCallback(() => {
        setIsOpen(false);
        setOnApplySuccess(null);
    }, []);

    const handleOnApply = useCallback(
        async (component: PartialComponent, isUniqueContentProperties: boolean) => {
            const response = await componentService.getComponentById(component.id.toString());

            if (response.status !== 200) {
                notify({
                    title: translate('components.create.modal.getComponent.error.badRequest'),
                    variant: 'error',
                });

                return;
            }

            applyComponent(response.json, isUniqueContentProperties);
            onApplySuccess?.();
            closeModal();
        },
        [translate, applyComponent, onApplySuccess, closeModal],
    );

    // ComponentModal methods
    const openComponentModal = useCallback(
        (
            mode: ComponentModalMode,
            elements: ControllableElement[],
            component?: PartialComponent,
            collectionId?: number,
        ) => {
            if (isComponentModalOpen) {
                return;
            }

            if (elements.length === 0 && mode === 'create') {
                return;
            }

            setComponentModalMode(mode);

            if (elements.length > 0) {
                setSelectedElements(elements);
            }

            if (collectionId) {
                setSelectedCollectionId(collectionId);
            }

            if (mode === 'edit' && component) {
                setSelectedComponent(component);
            }

            setComponentModalOpen(true);
        },
        [isComponentModalOpen],
    );

    const closeComponentModal = useCallback(() => {
        setComponentModalOpen(false);
        setSelectedElements([]);
    }, []);

    const handleComponentSave = useCallback(
        async ({
            name,
            description,
            collectionId,
            collectionName,
            id,
        }: {
            name: string;
            collectionId: number | null;
            description?: string;
            id?: number;
            collectionName?: string;
        }) => {
            setIsSaving(true);

            // Resolve collection ID
            let finalCollectionId = collectionId;

            if (collectionName) {
                const res = await componentCollectionService.addComponentCollection({
                    name: collectionName,
                });
                finalCollectionId = res.status === 200 ? res.json.id : null;
            } else if (collectionId) {
                finalCollectionId = Number(collectionId);
            }

            if (finalCollectionId === null) {
                setIsSaving(false);

                return;
            }

            // Create or edit component
            let res;

            if (componentModalMode === 'create') {
                const preparedComponent = createComponent(selectedElements);
                const mergedComponent: AddComponentRequest = {
                    name,
                    description: description || undefined,
                    collectionId: finalCollectionId,
                    elements: preparedComponent.elements,
                    textStyles: preparedComponent.textStyles || [],
                    contentProperties: preparedComponent.contentProperties,
                    type: preparedComponent.type,
                };
                res = await componentService.addComponent(mergedComponent);
            } else {
                if (id === undefined) {
                    setIsSaving(false);

                    return;
                }

                const payload: { name: string; collectionId: number; description?: string } = {
                    name,
                    collectionId: finalCollectionId,
                };

                if (description) {
                    payload.description = description;
                }

                res = await componentService.editComponent(id, payload);
            }

            // Handle response
            if (res.status === 200) {
                notify({
                    title: translate('components.create.modal.save.success'),
                    variant: 'success',
                });
                onComponentSaveSuccess?.(!selectedCollectionId);
            } else {
                notify({
                    title: translate('components.create.modal.save.error.badRequest'),
                    variant: 'error',
                });
            }

            setComponentModalOpen(false);

            // Clear selected elements after a delay
            setTimeout(() => {
                setSelectedElements([]);
                setIsSaving(false);
            }, 300);
        },
        [
            componentModalMode,
            createComponent,
            selectedElements,
            translate,
            onComponentSaveSuccess,
            selectedCollectionId,
        ],
    );

    const registerComponentSaveCallback = useCallback((callback: (resetCollection: boolean) => void) => {
        setOnComponentSaveSuccess(() => callback);
    }, []);

    const value = useMemo<ComponentsContextValue>(
        () => ({
            isOpen,
            openModal,
            closeModal,
            openComponentModal,
        }),
        [isOpen, openModal, closeModal, openComponentModal],
    );

    return (
        <ComponentsContext.Provider value={value}>
            {children}
            <ComponentOverview
                key={`overview_${isOpen}`} // Force remount to reset internal state when reopened
                isOpen={isOpen}
                onClose={closeModal}
                onApply={handleOnApply}
                isImageDesign={isImageDesign}
                registerComponentSaveCallback={registerComponentSaveCallback}
            />
            <ComponentModal
                key={`edit_create_${isComponentModalOpen}`} // Force remount to reset internal state when reopened
                isOpen={isComponentModalOpen}
                onClose={closeComponentModal}
                onSave={handleComponentSave}
                mode={componentModalMode}
                component={selectedComponent || undefined}
                suggestedName={
                    selectedElements.length > 0
                        ? [...selectedElements].sort(compareByRenderOrderDesc)[0].name
                        : 'New component'
                }
                isSaving={isSaving}
            />
        </ComponentsContext.Provider>
    );
};
